import { Spinner } from '@/components/glitch-ui/spinner';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DataTable } from '@/components/glitch-ui/data-table';
import { IconButton } from '@/components/glitch-ui/icon-button';
import { EmptyState } from '@/components/glitch-ui/empty-state';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { useToast } from '@/hooks/useToast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCampaignConversionActions,
  linkCampaignConversionAction,
  unlinkCampaignConversionAction,
  getConversionActions,
} from '@/lib/apis';
import type { ColumnDef } from '@tanstack/react-table';
import { CreateConversionForm } from '@/components/settings/create-conversion-form';

interface ConversionLink {
  id: number;
  is_primary: boolean;
  campaign_id: number;
  conversion_action_id: number;
  // Campaign's actual Google Ads conversion-goal scope: "CAMPAIGN" = these actions
  // drive a campaign-specific custom goal, "CUSTOMER" = inherits account defaults (#5456).
  goal_config_level?: string | null;
  conversion_action: {
    id: number;
    name: string;
    source: string;
    is_active: boolean;
    funnel_stage: string | null;
    action_type: string | null;
    default_value: number | null;
    primary_for_goal: boolean | null;
    google_ads_action_id: string;
    conversions_last_30d: number | null;
  } | null;
}

interface ConversionTrackingTabProps {
  campaignId: number;
  disabled?: boolean;
}

export function ConversionTrackingTab({ campaignId, disabled = false }: ConversionTrackingTabProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { symbol } = useCurrencySymbol();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addDialogTab, setAddDialogTab] = useState<'existing' | 'google' | 'hubspot'>('existing');
  const [selectedActionIds, setSelectedActionIds] = useState<Set<number>>(new Set());
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; actionId: number; name: string }>({
    isOpen: false,
    actionId: 0,
    name: '',
  });

  const { data: links = [], isLoading } = useQuery({
    queryKey: ['campaign', campaignId, 'conversion-actions'],
    queryFn: () => getCampaignConversionActions(campaignId),
  });

  const { data: allActions } = useQuery({
    queryKey: ['conversion-actions'],
    queryFn: () => getConversionActions(),
    enabled: showAddDialog,
  });

  const availableActions = (allActions?.conversion_actions || []).filter(
    (action) =>
      action.registry_id != null &&
      !links.some((link: ConversionLink) => link.conversion_action_id === action.registry_id)
  );

  const linkMutation = useMutation({
    mutationFn: (ids: number[]) =>
      Promise.all(ids.map((id) => linkCampaignConversionAction(campaignId, id))),
    onSuccess: (_, ids) => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId, 'conversion-actions'] });
      setShowAddDialog(false);
      setSelectedActionIds(new Set());
      toast({
        title: 'Conversions Linked',
        description: `${ids.length} conversion action${ids.length > 1 ? 's' : ''} linked to campaign`,
      });
    },
    onError: (error) => {
      toast({ title: 'Failed', description: error.message, variant: 'destructive' });
    },
  });

  const toggleAction = (id: number) => {
    setSelectedActionIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const unlinkMutation = useMutation({
    mutationFn: (conversionActionId: number) =>
      unlinkCampaignConversionAction(campaignId, conversionActionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId, 'conversion-actions'] });
      setDeleteModal({ isOpen: false, actionId: 0, name: '' });
      toast({ title: 'Conversion Unlinked', description: 'Conversion action removed from campaign' });
    },
    onError: (error) => {
      toast({ title: 'Failed', description: error.message, variant: 'destructive' });
    },
  });

  const sourceConfig: Record<string, { label: string; icon: string }> = {
    GOOGLE_TAG: { label: 'Google Tag', icon: '/google-ads.svg' },
    HUBSPOT: { label: 'HubSpot', icon: '/hubspot.png' },
    META: { label: 'Meta', icon: '/meta.png' },
  };

  const formatConversionType = (type: string | null | undefined): string | null => {
    const labels: Record<string, string> = {
      WEBPAGE: 'Website',
      PHONE_CALL: 'Phone Call',
      UPLOAD_CLICKS: 'Offline',
      UPLOAD_CALLS: 'Offline Call',
    };
    return type ? (labels[type] ?? null) : null;
  };

  const stageConfig: Record<string, { label: string; className: string }> = {
    LEAD: { label: 'Lead', className: 'bg-gray-100 text-gray-900' },
    MQL: { label: 'MQL', className: 'bg-blue-100 text-blue-800' },
    SQL: { label: 'SQL', className: 'bg-amber-100 text-amber-800' },
    CUSTOMER: { label: 'Customer', className: 'bg-green-100 text-green-800' },
  };

  const columns: ColumnDef<ConversionLink, unknown>[] = [
    {
      accessorKey: 'conversion_action.name',
      header: 'Name',
      cell: ({ row }) => {
        const action = row.original.conversion_action;
        return (
          <div>
            <div className="text-sm font-medium text-gray-900">{action?.name || '—'}</div>
            {formatConversionType(action?.action_type) && (
              <div className="text-sm font-normal text-gray-500">{formatConversionType(action?.action_type)}</div>
            )}
          </div>
        );
      },
    },
    {
      accessorFn: (row) => row.conversion_action?.source ?? '',
      id: 'source',
      header: 'Source',
      cell: ({ row }) => {
        const source = row.original.conversion_action?.source;
        if (source === 'PHONE_CALL') {
          return (
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-900">Phone Call</span>
            </div>
          );
        }
        const config = source ? sourceConfig[source] : null;
        return config ? (
          <div className="flex items-center gap-2">
            <img src={config.icon} alt="" className="h-4 w-4 object-contain" />
            <span className="text-sm text-gray-900">{config.label}</span>
          </div>
        ) : (
          <span className="text-sm text-gray-500">Unknown</span>
        );
      },
    },
    {
      accessorFn: (row) => row.conversion_action?.funnel_stage ?? '',
      id: 'funnel_stage',
      header: 'Stage',
      cell: ({ row }) => {
        const stage = row.original.conversion_action?.funnel_stage;
        const config = stage ? stageConfig[stage] : null;
        return config ? (
          <span className={`inline-flex items-center justify-center whitespace-nowrap px-3.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
            {config.label}
          </span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        );
      },
    },
    {
      accessorFn: (row) => row.conversion_action?.primary_for_goal ?? null,
      id: 'goal',
      header: 'Goal',
      cell: ({ row }) => {
        const primary = row.original.conversion_action?.primary_for_goal;
        return primary === true ? (
          <span className="inline-flex items-center justify-center whitespace-nowrap px-3.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            Primary
          </span>
        ) : primary === false ? (
          <span className="inline-flex items-center justify-center whitespace-nowrap px-3.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-900">
            Secondary
          </span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        );
      },
    },
    {
      accessorFn: (row) => row.goal_config_level ?? null,
      id: 'scope',
      header: 'Scope',
      cell: ({ row }) => {
        const isCampaignScoped = row.original.goal_config_level === 'CAMPAIGN';
        return isCampaignScoped ? (
          <span className="inline-flex items-center justify-center whitespace-nowrap px-3.5 py-0.5 rounded-full text-xs font-medium text-[#290BD9] bg-[#EDE9FE]">
            Campaign
          </span>
        ) : (
          <span className="inline-flex items-center justify-center whitespace-nowrap px-3.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-900">
            Account
          </span>
        );
      },
    },
    {
      accessorFn: (row) => row.conversion_action?.default_value ?? null,
      id: 'value',
      header: 'Value',
      cell: ({ row }) => {
        const val = row.original.conversion_action?.default_value;
        return val != null ? (
          <span className="text-sm font-medium text-gray-900">
            {`${symbol}${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        );
      },
    },
    {
      accessorFn: (row) => row.conversion_action?.conversions_last_30d ?? null,
      id: 'conversions',
      header: 'Conv',
      cell: ({ row }) => {
        const val = row.original.conversion_action?.conversions_last_30d;
        return val != null && val > 0
          ? <span className="text-sm text-gray-900">{val.toLocaleString()}</span>
          : <span className="text-sm text-gray-400">—</span>;
      },
    },
    {
      id: 'actions',
      header: () => null,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <IconButton
            variant="destructive"
            aria-label="Remove conversion"
            onClick={() =>
              setDeleteModal({
                isOpen: true,
                actionId: row.original.conversion_action_id,
                name: row.original.conversion_action?.name || 'this action',
              })
            }
          >
            <Trash2 className="h-4 w-4" />
          </IconButton>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" className="text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Conversions</h2>
          <p className="text-sm text-gray-500 mt-1">Link conversion actions to track performance for this campaign.</p>
        </div>
        <Button variant="outline" className="text-gray-900" onClick={() => setShowAddDialog(true)} disabled={disabled}>
          <Plus className="h-4 w-4 mr-2" />
          Add Conversion
        </Button>
      </div>

      {links.length > 0 ? (
        <DataTable
          data={links}
          columns={columns}
          variant="flush"
          pageSize={9999}
        />
      ) : (
        <EmptyState
          icon={<Plus className="h-12 w-12 text-gray-400" strokeWidth={1} />}
          title="No Conversions Linked"
          description="Link conversion actions to this campaign to track ROAS"
        />
      )}

      {/* Add Conversion Dialog */}
      <Dialog
        open={showAddDialog}
        onOpenChange={(open) => {
          setShowAddDialog(open);
          if (!open) {
            setSelectedActionIds(new Set());
            setAddDialogTab('existing');
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add Conversion</DialogTitle>
          </DialogHeader>

          {/* Source tabs */}
          <div className="flex gap-4 border-b border-gray-200">
            <button
              onClick={() => setAddDialogTab('existing')}
              className={cn(
                'px-1 pb-2.5 text-sm font-medium border-b-2 transition-colors -mb-px',
                addDialogTab === 'existing'
                  ? 'border-[#290BD9] text-[#290BD9]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              Existing
            </button>
            <button
              onClick={() => setAddDialogTab('google')}
              className={cn(
                'flex items-center gap-2 px-1 pb-2.5 text-sm font-medium border-b-2 transition-colors -mb-px',
                addDialogTab === 'google'
                  ? 'border-[#290BD9] text-[#290BD9]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <img src="/google-ads.svg" alt="" className="h-4 w-4" />
              Google Tag
            </button>
            <button
              onClick={() => setAddDialogTab('hubspot')}
              className={cn(
                'flex items-center gap-2 px-1 pb-2.5 text-sm font-medium border-b-2 transition-colors -mb-px',
                addDialogTab === 'hubspot'
                  ? 'border-[#290BD9] text-[#290BD9]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <img src="/hubspot.png" alt="" className="h-4 w-4" />
              HubSpot
            </button>
          </div>

          {addDialogTab === 'existing' ? (
            <>
              <div className="py-2 min-h-[360px]">
                {availableActions.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-6">
                    All conversion actions are already linked to this campaign.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                    {availableActions.map((action) => (
                      <label
                        key={action.registry_id}
                        className="flex items-center gap-4 p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <Checkbox
                          checked={selectedActionIds.has(action.registry_id!)}
                          onCheckedChange={() => toggleAction(action.registry_id!)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900">{action.name}</div>
                          {action.funnel_stage && (
                            <div className="text-xs text-gray-500 mt-0.5">{action.funnel_stage}</div>
                          )}
                        </div>
                        {action.source && (
                          <img
                            src={action.source === 'HUBSPOT' ? '/hubspot.png' : '/google-ads.svg'}
                            alt=""
                            className="h-4 w-4 shrink-0"
                          />
                        )}
                      </label>
                    ))}
                  </div>
                )}
                {availableActions.length > 0 && (
                  <p className="text-xs text-gray-500 text-center mt-3">
                    {selectedActionIds.size} of {availableActions.length} selected
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddDialog(false);
                    setSelectedActionIds(new Set());
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => linkMutation.mutate(Array.from(selectedActionIds))}
                  disabled={selectedActionIds.size === 0 || linkMutation.isPending}
                  className="bg-[#290BD9] hover:bg-[#2309C5] text-white"
                >
                  {linkMutation.isPending ? 'Linking...' : `Link${selectedActionIds.size > 0 ? ` ${selectedActionIds.size}` : ''}`}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <CreateConversionForm
              key={addDialogTab}
              showSourceTabs={false}
              defaultTab={addDialogTab}
              onCreated={(registryIds) => {
                linkMutation.mutate(registryIds);
                setAddDialogTab('existing');
              }}
              onCancel={() => setShowAddDialog(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <ConformationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, actionId: 0, name: '' })}
        onConfirm={() => unlinkMutation.mutate(deleteModal.actionId)}
        title="Remove Conversion Link"
        description={`Remove "${deleteModal.name}" from this campaign? This won't delete the conversion action itself.`}
        confirmText="Remove"
        cancelText="Cancel"
      />
    </div>
  );
}
